export enum AccountType {
  PASSENGER = "passenger",
  EXECUTIVE_ASSISTANT = "executive_assistant",
  TRAVEL_MANAGER = "travel_manager",
  DRIVER = "driver",
  OPERATOR = "operator",
  USER_ACCOUNT = "user_account",
}

/**
 * True when an account type manages other clients — i.e. an Executive Assistant or a Travel Manager.
 * The profile payload surfaces EA as either the enum value or the short "ea" alias, so both are matched.
 * Shared by `useCanManageAsAssistant` (logged-in account) and the operator Manage-Client view (managed account).
 *
 * NOTE: callers pass `profile.account_metadata.type`, which is NOT the source of truth — it's
 * a secondary/temporary flag copied from the account's metadata JSON and it CAN drift from the
 * authoritative `registered_as` column (that drift hid the EA tabs for 46 UAT accounts and needed a
 * one-off backfill migration to resync `metadata.type` to `registered_as`). The durable fix is to gate
 * on `registered_as` / the list endpoint's `account_type` (both derived straight from `registered_as`,
 * which is what the backend act-on-behalf gate itself checks). TODO: expose `registered_as` on the
 * profile DTO and key off that instead of `account_metadata.type`.
 */
export const isAssistantAccountType = (type?: string | null): boolean =>
  type === AccountType.EXECUTIVE_ASSISTANT || type === AccountType.TRAVEL_MANAGER || type === "ea";

/**
 * The account types that belong in the client portal rather than the operator dashboard.
 * The profile payload surfaces Executive Assistant as either the enum value or the short
 * "ea" alias, so both are matched.
 *
 * NOTE: callers read `profile.accountMetadata.type`, which is NOT the source of truth — it's a
 * copy of the authoritative `registered_as` column and it CAN drift from it. Only use this to
 * pick presentation, never to grant or deny access.
 */
export const CLIENT_ACCOUNT_TYPES: string[] = [
  AccountType.PASSENGER,
  AccountType.EXECUTIVE_ASSISTANT,
  AccountType.TRAVEL_MANAGER,
  "ea",
];

/**
 * True only when the account type is positively known to be a client one. An unknown, missing or
 * operator type returns false, so anything we can't confidently identify keeps its existing
 * behaviour — the fail-safe direction.
 */
export const isClientAccountType = (type?: string | null): boolean => !!type && CLIENT_ACCOUNT_TYPES.includes(type);
